// Lib imports
import { Button, Flex, InputRef } from 'antd';
import { useTranslation } from 'react-i18next';

// UI Components
import HeaderCard from 'components/HeaderCard';
import Show from 'components/Show';
import SwitchComponent from 'components/Switch';

// Filters
import AgronomistFilter from 'components/UniversalFilters/AgronomistFilter';
import PlantingGroupSearchFilter from 'components/UniversalFilters/PlantingGroupSearchFilter';
import RBCountryFilter from 'components/UniversalFilters/RBCountryFilter';
import TaskStatusFilter from 'components/UniversalFilters/TaskStatusFilter';
import TaskTypeFilter from 'components/UniversalFilters/TaskTypeFilter';

// Screen specific components
import TaskManagementLayout from './components/TaskManagementLayout';

// Hooks and utils
import useTaskManagementFilters from './hooks/useTaskManagementFilters';

// Types
import TaskDrawer from './components/task-drawer/TaskDrawer';
import { Task } from './types';

// Styles
import { useRef, useState } from 'react';
import './style.scss';
import { Outlet } from 'react-router-dom';

export type TaskManagementContext = {
  handleTaskRowClick: (record: Task) => void;
};

const TaskManagement = () => {
  const { t } = useTranslation();

  const {
    filters,
    hasActiveFilters,
    setCountry,
    setPlantingGroupName,
    setAgronomist,
    setManager,
    isManagerRole,
    setTaskStatus,
    setTaskType,
    setTodaysTasksVisibility,
    setDateRange,
    resetFilters,
  } = useTaskManagementFilters();

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const plantingGroupSearchRef = useRef<InputRef & { clearSearch: () => void }>(
    null,
  );

  const handleFilterReset = () => {
    plantingGroupSearchRef?.current?.clearSearch?.();
    resetFilters();
  };

  const handleTaskTableRowClick = (record: Task) =>
    setSelectedTaskId(record?.taskId);

  const handleDrawerClose = () => setSelectedTaskId(null);

  const contextValue: TaskManagementContext = {
    handleTaskRowClick: handleTaskTableRowClick,
  };

  return (
    <Flex vertical gap={16} className="task-management">
      <HeaderCard
        headerLabel={t('taskManagement.header')}
        headerInfo={t('taskManagement.subHeader')}
        filters={
          <RBCountryFilter
            selectedCountry={filters.country!}
            onChange={setCountry}
          />
        }
      />

      <TaskManagementLayout
        dateRange={filters.dateRange}
        onDateRangeChange={setDateRange}
      >
        <Flex gap={'10px 24px'} wrap="wrap" align="flex-start">
          <PlantingGroupSearchFilter
            ref={plantingGroupSearchRef}
            defaultValue={filters?.plantingGroupName || ''}
            onSearch={setPlantingGroupName}
            onClear={() => setPlantingGroupName(null)}
          />

          <TaskStatusFilter
            enabledValues={'ACTIVE,UPCOMING,INCOMPLETE,LATE,COMPLETED'}
            value={filters?.taskStatus || []}
            onChange={setTaskStatus}
          />
          <TaskTypeFilter
            value={filters?.taskType || []}
            onChange={setTaskType}
          />
          <AgronomistFilter
            country={filters?.country || 'Indonesia'}
            value={filters?.agronomist || []}
            onChange={setAgronomist}
            disabled={false}
          />

          <Show when={isManagerRole}>
            <AgronomistFilter
              label={t('common.filters.manager')}
              options={{ role: 'sr' }}
              country={filters?.country || 'Indonesia'}
              value={filters?.manager || []}
              onChange={setManager}
              disabled={false}
            />
          </Show>

          <SwitchComponent
            label={t('common.filters.todaysTasks')}
            isLoading={false}
            isChecked={filters?.isTodaysTasks}
            onChange={setTodaysTasksVisibility}
            disabled={false}
          />
          <Show when={hasActiveFilters}>
            <Button
              type="link"
              style={{ alignSelf: 'flex-end' }}
              onClick={handleFilterReset}
            >
              {t('common.clearAll')}
            </Button>
          </Show>
        </Flex>
        <Outlet context={contextValue} />
      </TaskManagementLayout>

      <TaskDrawer
        open={!!selectedTaskId}
        onClose={handleDrawerClose}
        taskId={selectedTaskId}
      />
    </Flex>
  );
};

export default TaskManagement;
